// src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    Paper,
    useTheme,
    Divider,
    Card,
    CardContent,
    Button,
    Container,
    Chip,
    Avatar,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    TableContainer,
    Table,
    TableRow,
    TableHead,
    TableCell,
    TableBody,
    Snackbar
} from '@mui/material';
import {
    ShoppingCart,
    Inventory,
    AttachMoney,
    TrendingUp,
    People,
    Warning,
    Refresh,
    Category,
    Receipt,
    Dashboard as DashboardIcon,
    Store,
    Info,
    AccountBalance,
    MoneyOff,
    Assessment,
    LocalShipping,
    CheckCircle,
    Pending,
    Cancel,
    Payments
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { 
    APIDashboardRecentOrders,
    APIDashboardStats 
} from '../apiServices/APIs';
import { formatNumberWithComma, recordsCount, getUserDetails } from '../utils/helpers';
import { apiService } from '../apiServices/ApiService';
import { usePermission } from '../hooks/usePermission';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorrState';

// Helper function outside component so it can be accessed anywhere
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount || 0);
};

// Stat Card Component - defined outside to avoid closure issues
const StatCard = ({ 
    icon, 
    title, 
    value, 
    subtitle, 
    color = 'primary', 
    isLoading = false,
    isCurrency = false 
}) => {
    const theme = useTheme();
    
    return (
        <Paper 
            elevation={2} 
            sx={{ 
                height: '100%',
                borderRadius: 3,
                bgcolor: `${color}.main`,
                color: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                },
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        width: 48,
                        height: 48,
                        mr: 2
                    }}>
                        {React.cloneElement(icon, { sx: { fontSize: 24 } })}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                            opacity: 0.9,
                            fontWeight: 500,
                            fontSize: '0.875rem'
                        }}>
                            {title}
                        </Typography>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold',
                            mt: 0.5
                        }}>
                            {isLoading ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : isCurrency ? (
                                formatCurrency(value)
                            ) : (
                                formatNumberWithComma(value)
                            )}
                        </Typography>
                    </Box>
                </Box>
                {subtitle && (
                    <Typography variant="caption" sx={{ 
                        opacity: 0.8,
                        display: 'block',
                        mt: 1,
                        fontSize: '0.75rem'
                    }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Paper>
    );
};

const Dashboard = () => {
    const theme = useTheme();
    const [recentOrders, setRecentOrders] = useState([]);
    const [dashboardStats, setDashboardStats] = useState({
        total_sales: 0,
        total_orders: 0,
        total_customers: 0,
        total_products: 0,
        total_categories: 0,
        total_suppliers: 0,
        total_expenses: 0,
        total_loans: 0,
        active_loans: 0,
        pending_orders: 0,
        low_stock_items: 0,
        out_of_stock_items: 0,
        today_sales: 0,
        today_orders: 0,
        total_revenue: 0,
        total_outstanding: 0
    });
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Use the updated permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    // Get user role
    const userRole = getUserDetails('roleId');
    const isAdmin = userRole === 'AD';

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchDashboardData();
        fetchDashboardStats();
    }, [showToast]);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIDashboardRecentOrders);

            if (result.success) {
                const orders = Array.isArray(result.data) ? result.data : [];
                setRecentOrders(orders);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load dashboard data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboardStats = async () => {
        setStatsLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIDashboardStats);

            if (result.success && result.data) {
                setDashboardStats(result.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    // Calculate items needing restock from low stock and out of stock items
    const itemsNeedingRestock = (dashboardStats.low_stock_items || 0) + (dashboardStats.out_of_stock_items || 0);

    // Calculate order totals for footer
    const calculateOrderTotals = () => {
        const totalAmount = recentOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        const completedOrders = recentOrders.filter(order => order.order_status === 'completed');
        const pendingOrders = recentOrders.filter(order => order.order_status === 'pending');
        const quotationOrders = recentOrders.filter(order => order.order_status === 'quotation');
        const directPaymentOrders = recentOrders.filter(order => order.order_type === 'direct_payment');
        const loanOrders = recentOrders.filter(order => order.order_type === 'loan_order');
        const cashOrders = recentOrders.filter(order => order.payment_method === 'cash');
        const mobileMoneyOrders = recentOrders.filter(order => order.payment_method === 'mobile_money');

        return {
            totalAmount,
            completedCount: completedOrders.length,
            completedAmount: completedOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0),
            pendingCount: pendingOrders.length,
            pendingAmount: pendingOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0),
            quotationCount: quotationOrders.length,
            quotationAmount: quotationOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0),
            directPaymentCount: directPaymentOrders.length,
            loanCount: loanOrders.length,
            cashCount: cashOrders.length,
            mobileMoneyCount: mobileMoneyOrders.length,
            totalCount: recentOrders.length
        };
    };

    const orderTotals = calculateOrderTotals();

    // Get Statistics Cards Data
    const getStatsData = () => {
        const stats = [];

        // Sales & Revenue Cards
        stats.push(
            {
                icon: <AttachMoney />,
                title: "Today's Sales",
                value: dashboardStats.today_sales || 0,
                subtitle: "Sales amount today",
                color: "success",
                isCurrency: true
            }
        );

        // Only add Total Sales card for admin users
        if (isAdmin) {
            stats.push({
                icon: <TrendingUp />,
                title: "Total Sales",
                value: dashboardStats.total_revenue || dashboardStats.total_sales || 0,
                subtitle: "Total revenue generated",
                color: "primary",
                isCurrency: true
            });
        }

        stats.push(
            {
                icon: <ShoppingCart />,
                title: "Today's Orders",
                value: dashboardStats.today_orders || 0,
                subtitle: "Orders placed today",
                color: "info",
                isCurrency: false
            },
            {
                icon: <Receipt />,
                title: "Total Orders",
                value: dashboardStats.total_orders || 0,
                subtitle: "All orders placed",
                color: "secondary",
                isCurrency: false
            }
        );

        // Customer & Product Cards
        stats.push(
            {
                icon: <People />,
                title: "Total Customers",
                value: dashboardStats.total_customers || 0,
                subtitle: "Registered customers",
                color: "info",
                isCurrency: false
            },
            {
                icon: <Inventory />,
                title: "Total Products",
                value: dashboardStats.total_products || 0,
                subtitle: "Available products",
                color: "warning",
                isCurrency: false
            },
            {
                icon: <Category />,
                title: "Total Categories",
                value: dashboardStats.total_categories || 0,
                subtitle: "Product categories",
                color: "secondary",
                isCurrency: false
            },
            {
                icon: <Store />,
                title: "Total Suppliers",
                value: dashboardStats.total_suppliers || 0,
                subtitle: "Active suppliers",
                color: "info",
                isCurrency: false
            }
        );

        // Loan & Expense Cards
        stats.push(
            {
                icon: <AccountBalance />,
                title: "Total Loans",
                value: dashboardStats.total_loans || 0,
                subtitle: "All loan orders",
                color: "warning",
                isCurrency: false
            },
            {
                icon: <TrendingUp />,
                title: "Active Loans",
                value: dashboardStats.active_loans || 0,
                subtitle: "Active customer loans",
                color: "error",
                isCurrency: false
            },
            {
                icon: <MoneyOff />,
                title: "Total Expenses",
                value: dashboardStats.total_expenses || 0,
                subtitle: "Total expenses recorded",
                color: "error",
                isCurrency: true
            },
            {
                icon: <Assessment />,
                title: "Outstanding",
                value: dashboardStats.total_outstanding || 0,
                subtitle: "Outstanding balance",
                color: "error",
                isCurrency: true
            }
        );

        // Stock Alert Cards
        stats.push(
            {
                icon: <Warning />,
                title: "Low Stock Items",
                value: dashboardStats.low_stock_items || 0,
                subtitle: "Items needing restock",
                color: (dashboardStats.low_stock_items || 0) > 0 ? "warning" : "success",
                isCurrency: false
            },
            {
                icon: <LocalShipping />,
                title: "Out of Stock",
                value: dashboardStats.out_of_stock_items || 0,
                subtitle: "Items out of stock",
                color: (dashboardStats.out_of_stock_items || 0) > 0 ? "error" : "success",
                isCurrency: false
            }
        );

        return stats;
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Dashboard Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={() => {
                    fetchDashboardData();
                    fetchDashboardStats();
                }}
                title="Unable to Load Dashboard"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 4
            }}>
                <Box>
                    <Typography variant="h5" sx={{ 
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        color: theme.palette.primary.main,
                        mb: 1
                    }}>
                        <DashboardIcon sx={{ 
                            mr: 2, 
                            fontSize: 30
                        }} />
                        Dashboard Overview
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Business performance and recent activities
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                        icon={<Store />}
                        label={`Branch: ${getUserDetails("branchName") || 'Main'}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                    <Tooltip title="Refresh dashboard data">
                        <IconButton 
                            color="primary" 
                            onClick={() => {
                                fetchDashboardData();
                                fetchDashboardStats();
                            }}
                            disabled={loading}
                            sx={{ 
                                p: 1.5,
                                backgroundColor: theme.palette.primary.light + '20',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.light + '30',
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Stock Alert Banner */}
            {itemsNeedingRestock > 0 && (
                <Alert 
                    severity="warning" 
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.warning.light}`,
                        backgroundColor: theme.palette.warning.light + '10'
                    }}
                    icon={<Warning />}
                    action={
                        <Button 
                            color="warning" 
                            size="small"
                            href="/inventory#/inventory"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                        >
                            View Inventory
                        </Button>
                    }
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ⚠️ Stock Alert: {itemsNeedingRestock} product(s) need attention
                    </Typography>
                    <Typography variant="body2">
                        {(dashboardStats.out_of_stock_items || 0)} out of stock,
                        {' '}{(dashboardStats.low_stock_items || 0)} low stock items
                    </Typography>
                </Alert>
            )}

            {/* Main Statistics Cards */}
            <Grid container spacing={3}>
                {getStatsData().map((stat, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                        <StatCard
                            icon={stat.icon}
                            title={stat.title}
                            value={stat.value}
                            subtitle={stat.subtitle}
                            color={stat.color}
                            isLoading={statsLoading}
                            isCurrency={stat.isCurrency}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Recent Orders Section */}
            <Box sx={{ mt: 4 }}>
                <Paper elevation={1} sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    height: '100%',
                    border: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.primary'
                        }}>
                            <Receipt sx={{ 
                                mr: 1.5, 
                                color: 'primary.main',
                                fontSize: 28
                            }} />
                            Recent Orders
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip 
                                label={`${recordsCount(dashboardStats.pending_orders || 0)} Pending`}
                                color="warning"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                            />
                            <Chip 
                                label={`Last ${recentOrders.length} orders`}
                                color="primary"
                                size="small"
                                variant="outlined"
                                icon={<Info fontSize="small" />}
                            />
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    
                    {recentOrders.length > 0 ? (
                        <Box sx={{ overflowX: 'auto', borderRadius: 2 }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            '& th': {
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem'
                                            }
                                        }}>
                                            <TableCell>#</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="center">Payment Method</TableCell>
                                            <TableCell align="center">Order Type</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recentOrders.map((order, index) => (
                                            <TableRow 
                                                key={order.id}
                                                hover
                                                sx={{ 
                                                    '&:last-child td': { borderBottom: 0 },
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.action.hover
                                                    },
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Avatar sx={{ 
                                                            width: 32, 
                                                            height: 32,
                                                            mr: 1.5,
                                                            backgroundColor: theme.palette.primary.light,
                                                            color: 'white',
                                                            fontSize: 14,
                                                            fontWeight: 600
                                                        }}>
                                                            {order.customer_name?.charAt(0).toUpperCase() || 'C'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                                                {order.customer_name}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                {order.customer_phone || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                        {new Date(order.created_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                                    {formatCurrency(order.total_amount)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={order.payment_method?.toUpperCase() || 'N/A'}
                                                        size="small"
                                                        color={order.payment_method === 'cash' ? 'success' : 'primary'}
                                                        sx={{ 
                                                            borderRadius: 1,
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            textTransform: 'capitalize'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={order.order_type === 'direct_payment' ? 'Direct' : 'Loan'}
                                                        size="small"
                                                        color={order.order_type === 'direct_payment' ? 'secondary' : 'warning'}
                                                        variant="outlined"
                                                        sx={{ 
                                                            borderRadius: 1,
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            textTransform: 'capitalize'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {order.order_status === 'pending' ? (
                                                        <Chip 
                                                            label="Pending" 
                                                            size="small" 
                                                            color="warning"
                                                            sx={{ 
                                                                borderRadius: 1,
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    ) : order.order_status === 'completed' ? (
                                                        <Chip 
                                                            label="Completed" 
                                                            size="small" 
                                                            color="success"
                                                            sx={{ 
                                                                borderRadius: 1,
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    ) : order.order_status === 'quotation' ? (
                                                        <Chip 
                                                            label="Quotation" 
                                                            size="small" 
                                                            color="info"
                                                            sx={{ 
                                                                borderRadius: 1,
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip 
                                                            label={order.order_status || 'Unknown'} 
                                                            size="small" 
                                                            color="default"
                                                            sx={{ 
                                                                borderRadius: 1,
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem',
                                                                textTransform: 'capitalize'
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    
                                    {/* Footer with Totals - Professional Style */}
                                    <TableBody>
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.primary.light + '15',
                                            borderTop: '3px solid',
                                            borderTopColor: theme.palette.primary.main,
                                            '& td': {
                                                fontWeight: 'bold',
                                                py: 2.5
                                            }
                                        }}>
                                            <TableCell colSpan={2} align="right">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Totals ({orderTotals.totalCount} orders):
                                                </Typography>
                                            </TableCell>
                                            <TableCell></TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                        {formatCurrency(orderTotals.totalAmount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total Amount
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <Chip
                                                        icon={<Payments />}
                                                        label={`Cash: ${orderTotals.cashCount}`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        icon={<Payments />}
                                                        label={`Mobile: ${orderTotals.mobileMoneyCount}`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <Chip
                                                        icon={<ShoppingCart />}
                                                        label={`Direct: ${orderTotals.directPaymentCount}`}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        icon={<AccountBalance />}
                                                        label={`Loan: ${orderTotals.loanCount}`}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label={`${orderTotals.completedCount} Completed`}
                                                        size="small"
                                                        color="success"
                                                    />
                                                    <Chip
                                                        icon={<Pending />}
                                                        label={`${orderTotals.pendingCount} Pending`}
                                                        size="small"
                                                        color="warning"
                                                    />
                                                    <Chip
                                                        icon={<Info />}
                                                        label={`${orderTotals.quotationCount} Quotes`}
                                                        size="small"
                                                        color="info"
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        
                                        {/* Summary Breakdown Row */}
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.background.default,
                                            '& td': {
                                                py: 2,
                                                borderBottom: '2px solid',
                                                borderBottomColor: theme.palette.primary.main
                                            }
                                        }}>
                                            <TableCell colSpan={2} align="right">
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.text.secondary }}>
                                                    Breakdown:
                                                </Typography>
                                            </TableCell>
                                            <TableCell></TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                                            Completed:
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                            {formatCurrency(orderTotals.completedAmount)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                                                            Pending:
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                            {formatCurrency(orderTotals.pendingAmount)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
                                                            Quotations:
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                            {formatCurrency(orderTotals.quotationAmount)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell colSpan={3} align="center">
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    gap: 2, 
                                                    flexWrap: 'wrap', 
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                }}>
                                                    <Paper elevation={0} sx={{ 
                                                        p: 1.5, 
                                                        bgcolor: theme.palette.success.light + '20',
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.success.light}`,
                                                        minWidth: 100
                                                    }}>
                                                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                                            Completed
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                            {orderTotals.completedCount}
                                                        </Typography>
                                                    </Paper>
                                                    <Paper elevation={0} sx={{ 
                                                        p: 1.5, 
                                                        bgcolor: theme.palette.warning.light + '20',
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.warning.light}`,
                                                        minWidth: 100
                                                    }}>
                                                        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                                                            Pending
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                            {orderTotals.pendingCount}
                                                        </Typography>
                                                    </Paper>
                                                    <Paper elevation={0} sx={{ 
                                                        p: 1.5, 
                                                        bgcolor: theme.palette.info.light + '20',
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.info.light}`,
                                                        minWidth: 100
                                                    }}>
                                                        <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
                                                            Quotations
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                                            {orderTotals.quotationCount}
                                                        </Typography>
                                                    </Paper>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            py: 6,
                            color: 'text.secondary'
                        }}>
                            <Receipt sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                No recent orders
                            </Typography>
                            <Typography>
                                Completed orders will appear here
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Dashboard;